import { motion } from "motion/react"
import { useState } from "react"
import { useSearchParams } from "react-router"

import Form from "@forento/ui/components/Form"

import { SubmitButton } from "~/components/Button"
import InputField from "~/components/InputField"
import Link from "~/components/Link"
import { useAlert } from "~/contexts/AlertContext"
import routes from "~/utilities/routes"
import trpc, { getInputErrors } from "~/utilities/trpc"

import Layout, { Logo } from "./Layout"
import { authCardClass, staggerContainer, staggerItem } from "./Layout/authCard"

const ResetPasswordPage: React.FC = () => {
	const alert = useAlert()
	const [searchParams] = useSearchParams()

	const [email, setEmail] = useState("")
	const [isSubmitting, setSubmitting] = useState(false)
	const [isEmailSent, setEmailSent] = useState(false)

	const handleResetPassword = async () => {
		if (email.trim().length === 0) return

		setSubmitting(true)
		try {
			const { status } = await trpc.user.resetCreatorPassword.mutate(email.trim())
			if (status !== "success") {
				await alert.show("Invalid email", "That email address isn't registered with Forento.")
				setSubmitting(false)
				return
			}
			setEmailSent(true)
		} catch (error) {
			setSubmitting(false)
			const errors = getInputErrors(error)
			if ("root" in errors) {
				await alert.show("Invalid email", "That email address isn't registered with Forento.")
			}
		}
		setSubmitting(false)
	}

	const nextUrl = searchParams.get("next")

	const signInUrl = nextUrl
		? `${routes.account.signin()}?next=${encodeURIComponent(nextUrl)}`
		: routes.account.signin()

	return (
		<Layout>
			<Logo />
			<Form onSubmit={handleResetPassword} className={authCardClass}>
				{isEmailSent ? (
					<motion.div
						className="flex w-full flex-col items-stretch gap-0"
						variants={staggerContainer}
						initial="hidden"
						animate="visible"
					>
						<motion.div variants={staggerItem}>
							<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
								Check your email
							</h1>
							<p className="mt-1.5 text-left text-sm text-zinc-500">
								We&apos;ve sent a password reset email to{" "}
								<strong className="text-zinc-800">{email.trim()}</strong>.
							</p>
						</motion.div>
						<motion.div variants={staggerItem} className="mt-6">
							<Link
								to={signInUrl}
								className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900"
							>
								← Back to sign in
							</Link>
						</motion.div>
					</motion.div>
				) : (
					<motion.div
						className="flex w-full flex-col items-stretch gap-0"
						variants={staggerContainer}
						initial="hidden"
						animate="visible"
					>
						<motion.div variants={staggerItem}>
							<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
								Forgot password?
							</h1>
							<p className="mt-1.5 text-left text-sm text-zinc-500">
								Don&apos;t worry, we&apos;ll send you reset instructions.
							</p>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-6">
							<InputField
								inputType="email"
								label="Email"
								placeholder="Enter your email"
								value={email}
								onChange={setEmail}
								autoComplete="email"
							/>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-5">
							<SubmitButton
								variant="primary"
								isLoading={isSubmitting}
								className="w-full cursor-pointer rounded-xl py-3 text-sm font-semibold"
							>
								Reset password
							</SubmitButton>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-6">
							<Link
								to={signInUrl}
								className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900"
							>
								← Back to sign in
							</Link>
						</motion.div>
					</motion.div>
				)}
			</Form>
		</Layout>
	)
}

export default ResetPasswordPage
